import {
  CreateWorkflowNode,
  MergeConfiguration,
  WorkflowEdge,
  WorkflowNode,
  WorkflowNodeType,
} from '../../../api/model.ts';
import { useStore } from '../../../store/store.ts';
import { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { SectionTitle } from 'components/form/SectionTitle';
import NodeDraggableRow from './components/NodeDraggableRow.tsx';
import { useNodeValidation } from 'hooks/useNodeValidation.tsx';
import FormBlock from 'components/form/FormBlock.tsx';
import { NodeFormProps } from 'api/workflows-steps/formSchema.tsx';
import clsx from 'clsx';

type Predecessor = {
  node_id: string;
  column: string | null;
  type: WorkflowNodeType;
  name: string;
};

const reorder = (list: Predecessor[], startIndex: number, endIndex: number): Predecessor[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const MergeSchemaForm = (props: NodeFormProps) => {
  const { nodeId, initialValues = {} as CreateWorkflowNode } = props;
  const { mainReactFlowInstance } = useStore();

  const [formValues, setFormValues] = useState<CreateWorkflowNode>(initialValues);

  const { validate } = useNodeValidation(initialValues.type);

  const isJoinStrategy = (formValues.configuration as MergeConfiguration)?.strategy === 'join';

  const handleFormChange = (data: CreateWorkflowNode | WorkflowNode) => {
    setFormValues(data);
    props.onChange?.(data);
  };

  const predecessors: Predecessor[] = useMemo(() => {
    const order: {
      [p: string]: {
        index: number;
        column: string;
      };
    } =
      (formValues.configuration as MergeConfiguration)?.join_order?.reduce(
        (prev, current, index) => ({
          ...prev,
          [current.node_id]: {
            index,
            column: current.column,
          },
        }),
        {},
      ) ?? {};

    return (
      mainReactFlowInstance
        ?.getEdges()
        .filter((e) => e.target === nodeId)
        .map((e) => mainReactFlowInstance.getNode(e.source))
        .filter(Boolean)
        .sort((a, b) => {
          if (!order) {
            return 0;
          }
          const orderA = order[a!.id]?.index ?? -1;
          const orderB = order[b!.id]?.index ?? -1;

          if (orderA === -1 && orderB === -1) return 0;
          if (orderA === -1) return 1;
          if (orderB === -1) return -1;

          return orderA - orderB;
        })
        .map((predecessor) => ({
          node_id: predecessor!.id,
          column: order[predecessor!.id]?.column ?? null,
          type: predecessor!.type as WorkflowNodeType,
          name: predecessor!.data.name,
        })) ?? []
    );
  }, [nodeId, mainReactFlowInstance, formValues, isJoinStrategy, JSON.stringify(mainReactFlowInstance?.getEdges())]);

  const [items, setItems] = useState<Predecessor[]>(predecessors);

  useEffect(() => {
    const ids = predecessors.map((n) => n.node_id);
    const newItems = items.filter((i) => ids.includes(i.node_id));

    const itemsIds = newItems.map((n) => n.node_id);
    const toAdd = predecessors.filter((p) => !itemsIds.includes(p.node_id));

    setItems(newItems.concat(toAdd));
  }, [predecessors]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    const newItems = reorder(items, result.source.index, result.destination.index);
    setItems(newItems);
  };

  const handleColumnChange = (value: string, index: number) => {
    setItems((prevState) => {
      const updatedItems = [...prevState];
      updatedItems[index] = {
        ...updatedItems[index],
        column: value,
      };
      return updatedItems;
    });
  };

  const handleSubmit = async () => {
    const configuration = isJoinStrategy
      ? {
          ...formValues.configuration,
          join_order: items.map((item) => ({
            node_id: item.node_id,
            column: item.column,
          })),
        }
      : {
          strategy: (formValues.configuration as MergeConfiguration).strategy,
        };

    const formData = { ...formValues, configuration };
    const isCreate = !initialValues?.name;
    if (!isCreate) {
      const validationExtraData = {
        edges: mainReactFlowInstance?.getEdges().filter((e) => e.target === formData.id) as WorkflowEdge[],
      };

      const isValid = await validate(formData, validationExtraData);
      if (!isValid) {
        return;
      }
    }
    props.onSubmit?.(formData);
  };

  return (
    <div className="flex flex-col gap-2">
      <FormBlock {...props} onSubmit={handleSubmit} onChange={handleFormChange} />
      {isJoinStrategy && (
        <div>
          <SectionTitle title="Join order and columns" />
          <div
            className={clsx('flex flex-col gap-2 p-4', {
              'pointer-events-none': props.disabled,
            })}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-24 items-center font-bold">
              <span className="col-span-1 text-center"></span>
              <span className="col-span-11 text-center">node name</span>
              <div className="col-span-11 text-center">column</div>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable" isDropDisabled={props.disabled}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="w-full">
                    {items.map((item, index) => (
                      <NodeDraggableRow
                        key={item.node_id}
                        onColumnChange={handleColumnChange}
                        id={item.node_id}
                        index={index}
                        column={item.column ?? ''}
                        name={item.name}
                        type={item.type}
                        disabled={props.disabled}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      )}
    </div>
  );
};

export default MergeSchemaForm;
